import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { notify } from "@/common/components/common/Notification";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import Loader from "@/common/components/ui/loader";
import { format } from "date-fns";

interface HolidayPayload {
  description: string;
  date: string;
  day: string;
}

const CreateHolidayPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = !!id;
  const isViewMode = location.pathname.includes("/view/");

  const [loading, setLoading] = useState(isEdit || isViewMode);
  const [formData, setFormData] = useState<HolidayPayload>({
    description: "",
    date: "",
    day: "",
  });

  const fetchHoliday = async () => {
    try {
      setLoading(true);
      // Mock data representing what the API might return based on `id`
      notify.error("Holiday fetch not fully implemented in API yet.");
      navigate("/admin/cms/holidays");
    } catch (error: any) {
      console.error(error);
      notify.error(error?.message ?? "Failed to fetch holiday details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((isEdit || isViewMode) && id) {
      fetchHoliday();
    }
  }, [id, isEdit, isViewMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "date" && value) {
      // Auto-fill day
      const dateObj = new Date(value);
      const dayName = format(dateObj, "EEEE");
      setFormData(prev => ({
        ...prev,
        [name]: value,
        day: dayName,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) {
      return;
    }

    try {
      setLoading(true);
      const payload = { ...formData };
      console.log("Saving payload (Mock):", payload);

      if (isEdit) {
        notify.success("", "Holiday updated successfully (Mock)");
      } else {
        notify.success("", "Holiday created successfully (Mock)");
      }
      navigate("/admin/cms/holidays");
    } catch (error: any) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to save holiday details";
      notify.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && (isEdit || isViewMode)) {
    return <Loader message="Loading Holiday Data..." />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 font-sans text-sm">
      {loading && !isEdit && !isViewMode && (
        <Loader message="Saving Holiday..." />
      )}
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        {/* Header */}
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-5xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() => navigate("/admin/cms/holidays")}
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {isViewMode
                      ? `View Holiday`
                      : isEdit
                        ? `Edit Holiday`
                        : "Create New Holiday"}
                  </h1>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    Configure holiday details and dates
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isViewMode ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => navigate("/admin/cms/holidays")}
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate("/admin/cms/holidays")}
                      className="h-11 rounded-md border border-slate-200 bg-white px-5 text-sm font-semibold text-gray-700 transition-all hover:bg-slate-50"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {isEdit ? "Save Changes" : "Create Holiday"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex w-full flex-1 overflow-auto bg-slate-50/50">
          <div className="mx-auto w-full max-w-5xl flex-1 p-6">
            <Card className="shadow-sm">
              <CardContent className="space-y-8 p-8">
                <div>
                  <h3 className="text-md mb-4 border-b pb-2 font-semibold text-primary">
                    Holiday Information
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="description">
                        Description (Holiday Title){" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder=""
                        disabled={isViewMode}
                        className="h-10 rounded-md border-slate-200 bg-slate-50 shadow-inner placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus-visible:ring-sky-500"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date">
                        Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                        className="h-10 rounded-md border-slate-200 focus-visible:ring-sky-500"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="day">
                        Day <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="day"
                        name="day"
                        value={formData.day}
                        placeholder="Auto-filled from date"
                        readOnly
                        disabled
                        className="h-10 rounded-md border-transparent bg-slate-50 text-slate-500 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateHolidayPage;
